/**
 * Прогресс обучения текущего пользователя (E4.6a): общий для страниц в памяти, сохраняется
 * в браузере под ключом компании и пользователя. Смена пользователя загружает его
 * собственный прогресс -- отметки прежнего не наследуются; изменение в другой вкладке
 * перечитывается.
 */

import { useCallback, useEffect } from 'react';
import { create } from 'zustand';
import { useSessionStore } from '@/stores/session';
import {
	emptyProgress,
	readTrainingProgress,
	saveTrainingProgress,
	trainingProgressKey,
	type ProgressWarning,
	type TrainingProgress,
} from './progress';

interface ProgressState {
	owner: string | null;
	progress: TrainingProgress;
	warning: ProgressWarning | null;
}

const useProgressStore = create<ProgressState>(() => ({
	owner: null,
	progress: emptyProgress(),
	warning: null,
}));

function ownerKey(companyId: string | null, userId: string | null): string | null {
	return companyId === null || userId === null ? null : `${companyId}:${userId}`;
}

function read(companyId: string | null, userId: string | null): Omit<ProgressState, 'owner'> {
	return companyId === null || userId === null
		? { progress: emptyProgress(), warning: null }
		: readTrainingProgress(companyId, userId);
}

export function useTrainingProgress() {
	const companyId = useSessionStore((s) =>
		s.user?.company_id == null ? null : String(s.user.company_id),
	);
	const userId = useSessionStore((s) => (s.user?.id == null ? null : String(s.user.id)));
	const key = ownerKey(companyId, userId);
	const state = useProgressStore();
	const current = state.owner === key;

	/* Страница при открытии видит сохранённое сейчас, а не снимок с прошлого открытия */
	useEffect(() => {
		const memory = useProgressStore.getState();
		/* Несохранённый прогресс в памяти не затирается устаревшим значением из браузера: браузер
		   не сохраняет его вовсе или хранит значение неизвестной схемы, которое не перезаписывается */
		const unsaved = memory.warning === 'storage_unavailable' || memory.warning === 'unknown_schema';
		if (memory.owner === key && unsaved) return;
		useProgressStore.setState({ owner: key, ...read(companyId, userId) });
	}, [companyId, key, userId]);

	/* Прогресс, изменённый в другой вкладке, перечитывается */
	useEffect(() => {
		if (companyId === null || userId === null) return undefined;
		const progressKey = trainingProgressKey(companyId, userId);
		const reload = (event: StorageEvent) => {
			if (event.key === null || event.key === progressKey) {
				useProgressStore.setState({ owner: key, ...read(companyId, userId) });
			}
		};
		window.addEventListener('storage', reload);
		return () => window.removeEventListener('storage', reload);
	}, [companyId, key, userId]);

	const update = useCallback(
		(change: (progress: TrainingProgress) => TrainingProgress) => {
			if (companyId === null || userId === null) return;
			const before = useProgressStore.getState();
			/* Изменение применяется к сохранённому в браузере -- отметки другой вкладки не
			   затираются; если браузер не читает или не сохраняет прогресс -- к прогрессу в памяти */
			const stored = read(companyId, userId);
			const memory = before.owner === key ? before : null;
			const base =
				memory !== null && (memory.warning !== null || stored.warning !== null)
					? memory.progress
					: stored.progress;
			const next = change(base);
			const { persisted } = saveTrainingProgress(companyId, userId, next);
			const warning = persisted ? null : (before.warning ?? 'storage_unavailable');
			useProgressStore.setState({ owner: key, progress: next, warning });
		},
		[companyId, key, userId],
	);

	return {
		progress: current ? state.progress : emptyProgress(),
		warning: current ? state.warning : null,
		update,
	};
}
